import type { AuthCall, AuthError } from '@skytech/auth'
import { AuthClient, decodeToken, authCall } from '@skytech/auth'
import { config } from '@skytech/config'

import { API_ROUTE_PROFILE } from '@/api/apiRoutes'
import queries from '@/api/queries'
import { queryClient } from '@/queryClient'
import type { Profile, TokenData } from '@/types'

import getUserFromToken from './getUserFromToken'

class RefreshTokenClient {
  private refreshTokenPromise: null | Promise<unknown> = null

  // To prevent multiple refresh-token-requests
  public async refreshToken() {
    if (this.refreshTokenPromise) return this.refreshTokenPromise

    this.refreshTokenPromise = refreshTokenData()

    return this.refreshTokenPromise.then(() => {
      this.refreshTokenPromise = null
    })
  }
}
const RefreshTokenRequestsClient = new RefreshTokenClient()

const appAuthCall = async (args: AuthCall) => {
  try {
    const results = await authCall(args)

    return results
  } catch (error) {
    const err: AuthError = error
    if (err.response.status === 401) {
      await RefreshTokenRequestsClient.refreshToken()
      return authCall(args)
    }

    throw err
  }
}

const refreshTokenData = async () => {
  // Ask for a new token based on the refresh token
  const tokenResponse: Response = await fetch(config.AUTH_API_URL + 'dealer/token', {
    mode: 'cors',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  })

  if (!tokenResponse.ok) {
    AuthClient.logOut({ isTriggeredProgrammatically: true })
    return
  }

  const { token }: { token: string } = await tokenResponse.json()
  const tokenData = decodeToken<TokenData>(token)

  const currentToken = AuthClient.getToken()

  const currentTokenData = currentToken ? decodeToken<TokenData>(currentToken) : null
  // In case the refreshed token belongs to a different user (multiple open tabs case)
  if (currentTokenData?.coid !== tokenData.coid) {
    const profileResponse: Response = await fetch(config.API_URL + API_ROUTE_PROFILE, {
      headers: {
        Authorization: 'Bearer ' + token,
      },
    })

    if (!profileResponse.ok) {
      AuthClient.logOut({ isTriggeredProgrammatically: true })
      return
    }

    const profile: Profile = await profileResponse.json()
    const queryCache = queryClient.getQueryCache()
    const queriesToOmit = [queries.user(), queries.profile(), queries.translationList()]
    queryCache.getAll().forEach(({ queryKey }) => {
      if (queriesToOmit.includes(queryKey as string[])) return
      queryClient.removeQueries(queryKey)
    })
    const updatedUser = getUserFromToken(tokenData)
    queryClient.setQueryData(queries.user(), updatedUser)
    queryClient.setQueryData(queries.profile(), profile)
  }

  AuthClient.setToken(token)
}

/**
 * Since we use two backend apis, we need to check if the path is in the new backend apis
 * If it is, we need to construct the path with the new api url
 * @param path string
 * @param isNewBackendApi boolean
 * @returns
 */
function getApiPath(path: string, isNewBackendApi = false) {
  return (isNewBackendApi ? config.DEALER_NEW_API_URL : config.API_URL) + path
}

export const get = async <T>(path: string, isNewBackendApi = false): Promise<T> => {
  const apiPath = getApiPath(path, isNewBackendApi)

  return appAuthCall({ path: apiPath, method: 'GET' })
}

export async function post<R = unknown>(
  path: string,
  data?: unknown,
  isNewBackendApi = false
): Promise<R> {
  const apiPath = getApiPath(path, isNewBackendApi)
  return appAuthCall({
    path: apiPath,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

export async function update<R = unknown>(
  path: string,
  data?: unknown,
  isNewBackendApi = false
): Promise<R> {
  const apiPath = getApiPath(path, isNewBackendApi)
  return appAuthCall({
    path: apiPath,
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

export async function put<R = unknown>(
  path: string,
  data?: unknown,
  isNewBackendApi = false
): Promise<R> {
  const apiPath = getApiPath(path, isNewBackendApi)
  return appAuthCall({
    path: apiPath,
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

export async function del<R = unknown>(path: string, isNewBackendApi = false): Promise<R> {
  const apiPath = getApiPath(path, isNewBackendApi)
  return appAuthCall({
    path: apiPath,
    method: 'DELETE',
  })
}
